import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useTransition,
  memo,
} from "react";
import { createPortal } from "react-dom";
import { axiosInstance, SERVER_ORIGIN } from "../lib/axios";
import { useAuthStore } from "../store/useAuthStore";
import { FileText, Smile, Clock, Calendar, Paperclip, Tag } from "lucide-react";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import getFileIcon from "../lib/getFileIcon";
import { getSocket } from "../socket";
import AssignTaskModal from "../components/AssignTaskModal";
import ProjectsSidebar from "../components/ProjectsSidebar";
import TasksFeedPanel from "../components/TasksFeedPanel";
import ChecklistFillModal from "../Checklist/ChecklistFillModal";
import { useLocation, useSearchParams } from "react-router-dom";
import "./ProjectCommunication.css";
import "./ProjectCommunicationFilters.css";
dayjs.extend(weekday);

const PAGE_SIZE = 80;
const FIRST_SNAPPY_BATCH = 30;

/* ---------- tiny className helper ---------- */
const cx = (...parts) => parts.filter(Boolean).join(" ");

/* ---------- Project desktop notification helper ---------- */
const showProjectDesktopNotification = ({ message, project, myUserId }) => {
  try {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted"
    ) {
      return;
    }

    const senderId = String(
      message?.sender?._id || message?.senderId?._id || message?.senderId || "",
    );

    // Never notify for my own outgoing message.
    if (senderId && senderId === String(myUserId || "")) return;

    const senderName =
      message?.sender?.name ||
      message?.senderName ||
      message?.sender?.username ||
      message?.sender?.email ||
      message?.senderLabel ||
      "User";

    const projectId = String(
      message?.project?._id ||
        message?.projectId ||
        message?.project ||
        project?._id ||
        "",
    );

    const projectName =
      message?.project?.projectName ||
      message?.project?.name ||
      message?.projectName ||
      project?.projectName ||
      project?.name ||
      "Project Communication";

    const typedText = String(
      message?.content || message?.text || message?.message || "",
    ).trim();

    const preview = typedText
      ? typedText
      : message?.fileName
        ? `📎 ${message.fileName}`
        : message?.file
          ? "📎 Document received"
          : "New project message";

    const notification = new Notification(`${senderName} • ${projectName}`, {
      body: preview.slice(0, 180),
      icon: "/sdc-notification.png",
      badge: "/sdc-notification.png",
      tag: message?._id
        ? `project-message-${message._id}`
        : `project-message-${Date.now()}`,
      requireInteraction: false,
    });

    notification.onclick = () => {
      try {
        window.focus();
      } catch {}

      try {
        const currentPath =
          window.location.pathname || "/project-communication";

        const url = projectId
          ? `${currentPath}?projectId=${encodeURIComponent(projectId)}`
          : currentPath;

        window.location.assign(url);
      } catch {}

      try {
        notification.close();
      } catch {}
    };
  } catch (err) {
    console.error("Project notification error:", err);
  }
};
/* ---------- Image helpers (GLOBAL) ---------- */
const isImageFile = (url = "") =>
  /\.(jpg|jpeg|png|gif|webp)$/i.test(String(url));

// FRONTEND ONLY
const openPreview = async (fileKey) => {
  try {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${SERVER_ORIGIN}/api/download?key=${encodeURIComponent(fileKey)}`,
      {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );

    if (!res.ok) {
      alert("⚠️ File not found ");
      return;
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank");
  } catch (err) {
    console.error("Preview error:", err);
  }
};

/* ---------- date / size helpers ---------- */
const formatDateGroup = (date) => {
  const today = dayjs();
  const d = dayjs(date);
  if (d.isSame(today, "day")) return "Today";
  if (d.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
  return d.format("dddd, MMM D");
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

const getFileSizeFromURL = async () => null;
/* ---------- tiny emoji list ---------- */
const EMOJIS = [
  "💼",
  "📊",
  "📈",
  "🗂️",
  "📝",
  "📅",
  "💻",
  "👍",
  "👏",
  "🙏",
  "🙌",
  "💪",
  "🔥",
  "🎉",
  "😀",
  "😁",
  "😂",
  "🤣",
  "😊",
  "😍",
  "😘",
  "🥰",
  "😎",
  "😇",
  "😉",
  "🤗",
  "🤩",
  "😅",
  "🙃",
  "😐",
  "😴",
  "🤔",
  "🙄",
  "😭",
  "🤝",
  "✨",
  "💯",
];

/* ---------- context menu ---------- */
const createContextMenu = (x, y, options) => {
  const existing = document.getElementById("context-menu");
  if (existing) existing.remove();
  const el = document.createElement("div");
  el.id = "context-menu";
  el.className = "pc-context-menu";
  el.style.top = `${y}px`;
  el.style.left = `${x}px`;
  options.forEach((opt) => {
    const item = document.createElement("div");
    item.textContent = opt.label;
    item.className = "pc-context-item";
    item.onclick = () => {
      opt.action?.();
      el.remove();
    };
    el.appendChild(item);
  });
  document.body.appendChild(el);
  setTimeout(() => {
    const closer = () => el.remove();
    document.addEventListener("click", closer, { once: true });
  }, 0);
};

/* ===========================================================
      TYPE INFERENCE HELPERS (robust)
      =========================================================== */
const looksLikeDiscussionText = (text) =>
  typeof text === "string" && /^Topic:\s*/i.test(text || "");

// NEW: detect decisions even if server misses type/decision object
const looksLikeDecisionText = (text = "") => {
  if (typeof text !== "string") return false;
  return (
    /\bFinal Decision:\s*/i.test(text) ||
    /(^|\n)\s*Lock:\s*(true|false)/i.test(text) ||
    /(^|\n)\s*DecideBy:\s*\d{4}-\d{2}-\d{2}/i.test(text)
  );
};

const inferType = (m) => {
  if (m?.type === "employee-log") return "employee-log";
  if (/^Employee Entry:/i.test(m?.content || "")) return "employee-log";

  if (m?.type === "decision") return "decision";
  if (m?.decision) return "decision";
  if (looksLikeDecisionText(m?.content)) return "decision";

  if (m?.type === "discussion") return "discussion";
  if (looksLikeDiscussionText(m?.content)) return "discussion";

  return null;
};

/* ===========================================================
      FILTER HELPERS (employee / checklist / work type)
      =========================================================== */
const FILTER_ANY_CHECKLIST = "__any__";
const FILTER_NO_CHECKLIST = "__none__";

// Reads Work type and checklist info from an "Employee Entry" message text
const getEntryMeta = (text = "") => {
  const lines = String(text || "").split("\n");

  const read = (label) => {
    const line = lines.find((l) => l.startsWith(`${label}:`));
    const value = line ? line.replace(`${label}:`, "").trim() : "";
    return value === "-" ? "" : value;
  };

  return {
    work: read("Work"),
    filledChecklist: read("FilledChecklist"),
    hasUploadedChecklist: Boolean(read("Checklist") && read("ChecklistLogId")),
  };
};

/* ===========================================================
      PERFORMANCE: message cache + fast normalize (with meta)
      =========================================================== */
const fastNormalize = (raw) => {
  const trimmed = Array.isArray(raw) ? raw.slice(-PAGE_SIZE) : [];
  const byId = {};
  for (let i = 0; i < trimmed.length; i++) byId[trimmed[i]._id] = trimmed[i];

  const grouped = {};
  for (let i = 0; i < trimmed.length; i++) {
    const m = trimmed[i];
    const key = dayjs(m.createdAt).format("YYYY-MM-DD");

    const entryType = inferType(m);

    const entry = {
      _id: m._id,
      sender:
        m?.sender?.name ||
        m?.sender?.username ||
        m?.sender?.email ||
        m?.senderLabel ||
        "System",
      senderId: m?.sender?._id || null,
      text: m.content,
      file: m.file,
      fileName: m.fileName,
      fileSize: m.fileSize,
      type: entryType,
      task:
        m.task ||
        (m.type === "task"
          ? {
              assignedToId: m.assignedToId || m.assignedTo,
              assignedToName: m.assignedToName,
              typeofProject: m.typeofProject,
              workType: m.workType,
              drawingDocuments: m.drawingDocuments,
              estimatedHours: m.estimatedHours,
              dueDate: m.dueDate,
              remark: m.remark,
            }
          : null),
      decision: m.decision || null, // keep server decision object
      replyTo: m.replyTo
        ? {
            sender:
              byId[m.replyTo?._id || m.replyTo]?.sender?.name ||
              byId[m.replyTo?._id || m.replyTo]?.sender?.username ||
              byId[m.replyTo?._id || m.replyTo]?.sender?.email ||
              "Someone",
            text:
              byId[m.replyTo?._id || m.replyTo]?.content ||
              byId[m.replyTo?._id || m.replyTo]?.fileName ||
              "",
            _id:
              byId[m.replyTo?._id || m.replyTo]?._id ||
              m.replyTo?._id ||
              m.replyTo,
          }
        : null,
      time: new Date(m.createdAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      createdAt: m.createdAt,
    };

    if (!grouped[key]) grouped[key] = [entry];
    else grouped[key].push(entry);
  }
  return grouped;
};

/* ----------------------------- UI: Chips ----------------------------- */
const Pill = ({ icon, children, tone = "default" }) => (
  <span className={cx("pc-pill", tone !== "default" && `pc-pill--${tone}`)}>
    {icon ? <span className="pc-pill__icon">{icon}</span> : null}
    {children}
  </span>
);

/* ============================ MENTIONS ============================ */
const makeMentionToken = (display, id) => `@{${display}|${id}}`;
const stripMentionTokens = (text) =>
  text.replace(/\@\{([^|}]+)\|([^}]+)\}/g, "@$1");
const extractMentionIds = (text) => {
  const ids = [];
  const re = /\@\{([^|}]+)\|([^}]+)\}/g;
  let m;
  while ((m = re.exec(text))) ids.push(m[2]);
  return ids;
};
const matchMentionAtCursor = (text, caretPos) => {
  const slice = text.slice(0, caretPos);
  const m = /(^|\s)@([a-zA-Z0-9._-]{0,30})$/.exec(slice);
  if (!m) return null;
  return { start: slice.length - m[2].length - 1, query: m[2] || "" };
};
const renderWithMentions = (text) => {
  const parts = [];
  let last = 0;
  const re = /\@\{([^|}]+)\|([^}]+)\}/g;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span
        key={`${m.index}-${m[2]}`}
        className="pc-mention"
        title={`@${m[1]}`}
      >
        @{m[1]}
      </span>,
    );
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};
const esc = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const displayToRaw = (text, members = []) => {
  if (!text) return "";
  const sorted = [...members].sort(
    (a, b) => (b.name || "").length - (a.name || "").length,
  );
  let out = text;
  for (const m of sorted) {
    if (!m?._id || !m?.name) continue;
    const rx = new RegExp(`@${esc(m.name)}(?![\\w})])`, "g");
    out = out.replace(rx, `@{${m.name}|${m._id}}`);
  }
  return out;
};

const MentionList = ({ open, items, activeIndex, onPick }) => {
  if (!open) return null;
  return (
    <div className="pc-mention-list">
      {items.length === 0 ? (
        <div className="pc-mention-empty">No matches</div>
      ) : (
        items.map((m, i) => (
          <div
            key={m._id || i}
            onMouseDown={(e) => {
              e.preventDefault();
              onPick(m);
            }}
            className={cx(
              "pc-mention-item",
              i === activeIndex && "pc-mention-item--active",
            )}
          >
            <span className="pc-mention-avatar">
              {m.name?.charAt(0)?.toUpperCase() || "U"}
            </span>
            <span className="pc-mention-name">{m.name}</span>
          </div>
        ))
      )}
    </div>
  );
};
/* ========================== /MENTIONS ========================== */

/* --------------------------- Task Card UI --------------------------- */
const TaskCard = ({ msg, isMe }) => {
  const t = msg.task || {};
  const assigneeName = t.assignedToName || t.assignedToId || "Member";

  return (
    <div className="pc-task">
      <div className="pc-task__head">
        <div className="pc-task__title">
          <span role="img" aria-label="task" className="pc-task__icon">
            🗒️
          </span>
          Task Assigned to {assigneeName}
        </div>
      </div>

      {(t.typeofProject || t.workType) && (
        <div className="pc-task__tags">
          {t.typeofProject && (
            <Pill tone="soft" icon={<Tag size={14} />}>
              {t.typeofProject}
            </Pill>
          )}
          {t.workType && (
            <Pill tone="soft" icon={<Tag size={14} />}>
              {t.workType}
            </Pill>
          )}
        </div>
      )}

      {Array.isArray(t.drawingDocuments) && t.drawingDocuments.length > 0 && (
        <div className="pc-task__docs">
          <div className="pc-task__docs-title">
            <Paperclip size={12} />
            <span>Drawings &amp; Documents</span>
          </div>
          <div className="pc-task__docs-list">
            {t.drawingDocuments.map((d, i) => (
              <span key={i} className="pc-doc-chip">
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {t.remark ? (
        <div className="pc-task__remark">
          <span role="img" aria-label="remark">
            📝
          </span>
          <em>{t.remark}</em>
        </div>
      ) : null}

      <div className="pc-task__meta">
        {t.estimatedHours != null && (
          <div className="pc-task__meta-item">
            <Clock size={14} />
            <span>Est: {Number(t.estimatedHours).toFixed(2)} h</span>
          </div>
        )}
        {t.dueDate && (
          <div className="pc-task__meta-item">
            <Calendar size={14} />
            <span>
              Due:&nbsp;
              {dayjs(t.dueDate).isValid()
                ? dayjs(t.dueDate).format("DD MMM YYYY")
                : String(t.dueDate)}
            </span>
          </div>
        )}
      </div>

      <div className="pc-task__footer">
        <span>
          <strong>By:</strong> {isMe ? "You" : msg.sender}
        </span>
      </div>
    </div>
  );
};

/* ------------------------ Employee Log Card ------------------------ */
const EmpItem = ({ icon, label, value }) => (
  <div className="pc-emp-item">
    <div className="pc-emp-item__icon">{icon}</div>
    <div className="pc-emp-item__content">
      <div className="pc-emp-item__label">{label}</div>
      <div className="pc-emp-item__value">{value}</div>
    </div>
  </div>
);

const EmployeeLogCard = ({ msg, isMe }) => {
  const lines = String(msg.text || "").split("\n");

  const getVal = (label) =>
    lines
      .find((l) => l.startsWith(label + ":"))
      ?.replace(label + ":", "")
      .trim() || "-";

  // Checklist file uploaded with this log entry (if any)
  const checklistName = getVal("Checklist");
  const checklistLogId = getVal("ChecklistLogId");
  const hasChecklist = checklistName !== "-" && checklistLogId !== "-";

  // Filled checklist (ERP checklist page) attached to this log entry (if any)
  const filledChecklistName = getVal("FilledChecklist");
  const filledChecklistId = getVal("FilledChecklistId");
  const hasFilledChecklist =
    filledChecklistName !== "-" && filledChecklistId !== "-";

  const [checklistConfig, setChecklistConfig] = useState(null);
  const [openingChecklist, setOpeningChecklist] = useState(false);

  // Files the browser can show directly in a new tab
  const OPENABLE_TYPES = {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    txt: "text/plain",
  };

  const openChecklist = async () => {
    const ext = String(checklistName || "")
      .split(".")
      .pop()
      .toLowerCase();
    const mime = OPENABLE_TYPES[ext];

    // open the tab first (inside the click) so the popup is not blocked
    const win = mime ? window.open("", "_blank") : null;

    try {
      const res = await axiosInstance.get(`/logs/checklist/${checklistLogId}`, {
        responseType: "blob",
      });

      if (mime && win) {
        const blob = new Blob([res.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        win.location.href = url;
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        return;
      }

      // file type the browser cannot show (Excel, Word, CSV...) -> download
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = checklistName || "checklist";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Checklist open failed:", err);
      try {
        win?.close();
      } catch {}
      const status = err?.response?.status;
      alert(
        status === 403
          ? "❌ You are not allowed to open this checklist."
          : status === 404
            ? "❌ Checklist file was not found."
            : "❌ Checklist could not be opened.",
      );
    }
  };

  // Open the filled checklist (read-only; project members can comment on items)
  const openFilledChecklist = async () => {
    if (openingChecklist) return;
    setOpeningChecklist(true);

    try {
      const res = await axiosInstance.get(`/checklists/${filledChecklistId}`);
      const doc = res.data || {};
      const htmlPath = doc.templateId?.htmlPath;

      if (!htmlPath) {
        alert("❌ This checklist template is not available.");
        return;
      }

      setChecklistConfig({
        mode: "assignment",
        title: doc.templateTitle || filledChecklistName,
        htmlPath,
        assignmentId: doc._id,
        projectName: doc.projectName,
        data: doc.data || {},
        readOnly: true,
        defaults: {},
        users: [],
      });
    } catch (err) {
      console.error("Filled checklist open failed:", err);
      const status = err?.response?.status;
      alert(
        status === 403
          ? "❌ You are not allowed to open this checklist."
          : status === 404
            ? "❌ Checklist was not found."
            : "❌ Checklist could not be opened.",
      );
    } finally {
      setOpeningChecklist(false);
    }
  };

  return (
    <div className="pc-emp">
      {/* HEADER */}
      <div className="pc-emp__header">
        <div>
          <div className="pc-emp__title">👨‍💻 Employee Work Entry</div>
          <div className="pc-emp__sub">
            {isMe ? "Submitted by You" : msg.sender}
          </div>
        </div>
        <div className="pc-emp__time">{msg.time}</div>
      </div>

      {/* BODY */}
      <div className="pc-emp__body">
        <EmpItem icon="📝" label="Work Type" value={getVal("Work")} />
        <EmpItem icon="📄" label="Drawing" value={getVal("Drawing")} />
        <EmpItem icon="🔄" label="Version" value={getVal("Version")} />

        <div className="pc-emp__stats">
          <div className="pc-emp-stat pc-emp-stat--est">
            <div className="pc-emp-stat__label">Estimated</div>
            <div className="pc-emp-stat__value">
              {getVal("Estimated Hours")}
            </div>
            <div className="pc-emp-stat__unit">Hours</div>
          </div>

          <div className="pc-emp-stat pc-emp-stat--act">
            <div className="pc-emp-stat__label">Actual</div>
            <div className="pc-emp-stat__value">{getVal("Actual Hours")}</div>
            <div className="pc-emp-stat__unit">Hours</div>
          </div>
        </div>

        <div className="pc-emp__datetime">
          <span>📅 {getVal("Date")}</span>
          <span>🕒 {getVal("Time")}</span>
        </div>

        {/* CHECKLIST LINK (uploaded file) */}
        {hasChecklist && (
          <button
            type="button"
            onClick={openChecklist}
            title="Click to open checklist"
            className="pc-emp__checklist"
          >
            <span className="pc-emp__checklist-icon">📎</span>
            <span className="pc-emp__checklist-text">
              <span className="pc-emp__checklist-label">Checklist</span>
              <span className="pc-emp__checklist-name">{checklistName}</span>
            </span>
            <span className="pc-emp__checklist-arrow">↗️</span>
          </button>
        )}

        {/* FILLED CHECKLIST (ERP checklist page, comments allowed) */}
        {hasFilledChecklist && (
          <button
            type="button"
            onClick={openFilledChecklist}
            disabled={openingChecklist}
            title="Open the filled checklist and add comments"
            className="pc-emp__checklist"
          >
            <span className="pc-emp__checklist-icon">📋</span>
            <span className="pc-emp__checklist-text">
              <span className="pc-emp__checklist-label">Filled Checklist</span>
              <span className="pc-emp__checklist-name">
                {openingChecklist ? "Opening..." : filledChecklistName}
              </span>
            </span>
            <span className="pc-emp__checklist-arrow">↗️</span>
          </button>
        )}

        {getVal("Remark") !== "-" && (
          <div className="pc-emp__remark">
            <div className="pc-emp__remark-title">Remark</div>
            <div>{getVal("Remark")}</div>
          </div>
        )}
      </div>

      {/* Checklist popup (rendered on the page, outside the chat bubble) */}
      {checklistConfig &&
        createPortal(
          <ChecklistFillModal
            config={checklistConfig}
            onClose={() => setChecklistConfig(null)}
          />,
          document.body,
        )}
    </div>
  );
};

// Put this ABOVE DiscussionCard and MessageRow
const ReplyItem = ({ reply, isMe, isMobile, ensureSize, triggerDownload }) => {
  const ext = reply.fileName?.split(".").pop()?.toLowerCase();
  return (
    <div
      className={cx("pc-reply", isMe && "pc-reply--me")}
      onMouseEnter={() => ensureSize(reply)}
    >
      <div className="pc-reply__head">
        {isMe ? "You" : reply.sender} •{" "}
        <span className="pc-reply__time">{reply.time}</span>
      </div>

      {reply.file ? (
        <div className="pc-file-row pc-file-row--small">
          {getFileIcon(ext)}
          <div className="pc-file-row__info">
            <div
              className={cx(
                "pc-file-row__name",
                isMobile && "pc-file-row__name--mobile",
              )}
              title={reply.fileName}
            >
              {reply.fileName}
            </div>
            <div className="pc-file-row__size">
              {reply.fileSizeDisplay || "…"}
            </div>
          </div>
          <button
            onClick={() => triggerDownload(reply.file, reply.fileName)}
            className="pc-file-row__btn"
            title="Download"
          >
            ⬇️
          </button>
        </div>
      ) : (
        <div className="pc-reply__text">
          {renderWithMentions(reply.text || "")}
        </div>
      )}
    </div>
  );
};

/* ----------------------- Discussion helpers & UI ----------------------- */
const parseDiscussion = (text = "") => {
  const first = (text.split("\n")[0] || "").trim();
  const m = /^Topic:\s*(.+)$/i.exec(first);
  return {
    topic: m ? m[1] : null,
    body: m ? text.split("\n").slice(2).join("\n") : text,
  };
};

/* --------------------------- Discussion Card --------------------------- */
const DiscussionCard = ({
  msg,
  onReply,
  replies,
  authUserId,
  ensureSize,
  triggerDownload,
  isMobile,
}) => {
  const { topic, body } = useMemo(
    () => parseDiscussion(msg.text || ""),
    [msg.text],
  );

  return (
    <div className="pc-disc">
      <div className="pc-disc__head">
        <Pill tone="violet">🗣️ Discussion</Pill>
        <span className="pc-disc__time">{msg.time}</span>
      </div>

      {topic && <div className="pc-disc__topic">{topic}</div>}
      {body && <div className="pc-disc__body">{renderWithMentions(body)}</div>}

      {msg.file && (
        <div
          className="pc-file-row pc-file-row--violet"
          onMouseEnter={() => ensureSize(msg)}
        >
          {getFileIcon(msg.fileName?.split(".").pop()?.toLowerCase())}
          <div className="pc-file-row__info">
            <div
              title={msg.fileName}
              className={cx(
                "pc-file-row__name",
                isMobile && "pc-file-row__name--mobile",
              )}
            >
              {msg.fileName}
            </div>
            <div className="pc-file-row__size">
              {msg.fileSizeDisplay || "…"}
            </div>
          </div>
          <button
            onClick={() => triggerDownload(msg.file, msg.fileName)}
            className="pc-file-row__btn"
            title="Download"
          >
            ⬇️
          </button>
        </div>
      )}

      <div className="pc-disc__replies">
        {replies.map((r) => (
          <ReplyItem
            key={r._id}
            reply={r}
            isMe={String(r.senderId || "") === String(authUserId || "")}
            isMobile={isMobile}
            ensureSize={ensureSize}
            triggerDownload={triggerDownload}
          />
        ))}
      </div>

      <div className="pc-disc__footer">
        <button
          type="button"
          onClick={onReply}
          className="pc-disc__reply-btn"
          title="Reply to this discussion"
        >
          Reply
        </button>

        <span className="pc-disc__author">
          —{" "}
          {String(msg.senderId || "") === String(authUserId || "")
            ? "You"
            : msg.sender}
        </span>
      </div>
    </div>
  );
};

/* --------------------------- Decision Card --------------------------- */
const DecisionCard = ({ msg, isMe, ensureSize, triggerDownload, isMobile }) => {
  const text = msg.text || "";
  const d = msg.decision || {};

  // ---- extract fields ----
  const topic = d.topic || /^Topic:\s*(.+)$/im.exec(text)?.[1]?.trim() || "";
  const finalDecision =
    d.finalDecision ||
    (
      /^Final Decision:\s*([\s\S]*?)(?:\n(?:DecideBy:|Lock:)|$)/im.exec(
        text,
      )?.[1] || ""
    ).trim();
  const decideByRaw =
    d.decideBy ||
    /^DecideBy:\s*([0-9]{4}-[0-9]{2}-[0-9]{2})/im.exec(text)?.[1] ||
    "";
  const decideBy =
    decideByRaw && dayjs(decideByRaw).isValid()
      ? dayjs(decideByRaw).format("DD MMM YYYY")
      : "";
  const lock = d.lock === true || /(^|\n)\s*Lock:\s*true/i.test(text);
  const byName = d.byName || msg.sender || (isMe ? "You" : "") || "—";
  const lockedBy = lock ? d.lockedBy || byName || "—" : "—";

  const ext = msg.fileName?.split(".").pop()?.toLowerCase();

  return (
    <div className="pc-dec">
      {/* header */}
      <div className="pc-dec__head">
        <span className="pc-dec__title">🧭 Decision</span>
        <span className="pc-dec__time">{msg.time}</span>
      </div>

      {/* Topic */}
      <div className="pc-dec__label">Topic:</div>
      <div className="pc-dec__field">{topic || "—"}</div>

      {/* By */}
      <div className="pc-dec__by">
        <div>
          <span className="pc-dec__by-label">By:</span>
          <span className="pc-dec__by-name">{byName}</span>
        </div>
      </div>

      {/* Final Decision */}
      <div className="pc-dec__label">Final Decision:</div>
      <div className="pc-dec__field pc-dec__field--mb">
        {finalDecision || "—"}
      </div>

      {/* Attachment (if any) */}
      {msg.file && (
        <div
          className="pc-file-row pc-file-row--green"
          onMouseEnter={() => ensureSize(msg)}
        >
          {getFileIcon(ext)}
          <div className="pc-file-row__info">
            <div
              title={msg.fileName}
              className={cx(
                "pc-file-row__name",
                isMobile && "pc-file-row__name--mobile",
              )}
            >
              {msg.fileName}
            </div>
            <div className="pc-file-row__size">
              {msg.fileSizeDisplay || "…"}
            </div>
          </div>
          <button
            onClick={() => triggerDownload(msg.file, msg.fileName)}
            className="pc-file-row__btn"
            title="Download"
          >
            ⬇️
          </button>
        </div>
      )}

      {/* Chips */}
      <div className="pc-dec__chips">
        <span className="pc-chip pc-chip--blue">
          <Calendar size={14} /> Decide by: {decideBy || "—"}
        </span>
        <span
          className={cx(
            "pc-chip",
            lock ? "pc-chip--locked" : "pc-chip--unlocked",
          )}
        >
          🔒 {lock ? "Locked" : "Unlocked"}
        </span>
        <span className="pc-chip pc-chip--indigo">
          Lock by: {lock ? lockedBy : "—"}
        </span>
      </div>

      {/* bottom-right time */}
      <div className="pc-dec__foot-time">{msg.time}</div>
    </div>
  );
};

/* --------------------------- Message Row --------------------------- */

const MessageRow = memo(function MessageRow({
  msg,
  isMe,
  isMobile,
  onContext,
  ensureSize,
  triggerDownload,
  authUserId,
  onReply,
  threadReplies = [],
}) {
  const [imageUrl, setImageUrl] = useState(null); // ✅ HERE

  useEffect(() => {
    if (!msg.file || !isImageFile(msg.fileName)) return;

    let active = true;

    const loadImage = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(
          `${SERVER_ORIGIN}/api/download?key=${encodeURIComponent(msg.file)}`,
          {
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );

        if (!res.ok) {
          setImageUrl("FILE_NOT_FOUND");
          return;
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);

        if (active) setImageUrl(url);
      } catch (err) {
        console.error("Image load failed", err);
      }
    };

    loadImage();

    return () => {
      active = false;
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [msg.file]);

  const looksLikeDiscussionFlag =
    msg.type === "discussion" ||
    (looksLikeDiscussionText(msg.text) && !looksLikeDecisionText(msg.text));

  const longPress = (cb) => {
    let timer = null;
    return {
      onTouchStart: (e) => {
        timer = setTimeout(() => cb(e), 550);
      },
      onTouchEnd: () => clearTimeout(timer),
      onTouchMove: () => clearTimeout(timer),
    };
  };

  const pressHandlers = longPress((touchEvt) => {
    const t = touchEvt.touches?.[0];
    if (!t) return;
    onContext(
      { preventDefault: () => {}, clientX: t.clientX, clientY: t.clientY },
      msg,
      isMe,
    );
  });

  const wasIMentioned =
    (msg.text || "").includes("@{") &&
    (msg.text || "").includes("|") &&
    (msg.text || "").includes("}") &&
    /@\{[^|}]+\|([^}]+)\}/.test(msg.text || "") &&
    RegExp.$1 === String(authUserId || "");

  const isLockedDecision =
    msg.type === "decision" &&
    (msg.decision?.lock === true || /Lock:\s*true/i.test(msg.text || ""));

  return (
    <div className={cx("pc-row", isMe && "pc-row--me")}>
      <div
        className={cx(
          "pc-bubble",
          isMe && "pc-bubble--me",
          isMobile && "pc-bubble--mobile",
        )}
        onContextMenu={(e) => onContext(e, msg, isMe)}
        {...pressHandlers}
      >
        {msg.replyTo && !looksLikeDiscussionFlag && (
          <div className="pc-quote">
            <strong>{msg.replyTo.sender}:</strong>{" "}
            {msg.replyTo.text || msg.replyTo.fileName}
          </div>
        )}

        {msg.type === "task" || msg.task ? (
          <TaskCard msg={msg} isMe={isMe} />
        ) : msg.type === "employee-log" ? (
          <EmployeeLogCard msg={msg} isMe={isMe} />
        ) : msg.type === "decision" ? (
          <DecisionCard
            msg={msg}
            isMe={isMe}
            ensureSize={ensureSize}
            triggerDownload={triggerDownload}
            isMobile={isMobile}
          />
        ) : looksLikeDiscussionFlag && !msg.replyTo ? (
          <DiscussionCard
            msg={msg}
            onReply={() => onReply(msg)}
            replies={threadReplies}
            authUserId={authUserId}
            ensureSize={ensureSize}
            triggerDownload={triggerDownload}
            isMobile={isMobile}
          />
        ) : msg.file ? (
          <div className={cx("pc-file-wrap", isMe && "pc-file-wrap--me")}>
            {/* ================= IMAGE FILE ================= */}
            {isImageFile(msg.fileName) ? (
              <div
                className={cx(
                  "pc-img-card",
                  isMe && "pc-img-card--me",
                  isMobile && "pc-img-card--mobile",
                )}
                onClick={() => openPreview(msg.file)}
              >
                {imageUrl === "FILE_NOT_FOUND" ? (
                  <div className="pc-img-card__missing">
                    ⚠️ File not found in S3
                  </div>
                ) : imageUrl ? (
                  <img src={imageUrl} alt="" className="pc-img-card__img" />
                ) : (
                  <div className="pc-img-card__placeholder" />
                )}

                {!msg.file && (
                  <div className="pc-file-card__time">{msg.time}</div>
                )}
              </div>
            ) : msg.fileName?.toLowerCase().endsWith(".pdf") ? (
              /* ================= PDF FILE ================= */
              <div
                className={cx(
                  "pc-file-card",
                  isMe && "pc-file-card--me",
                  isMobile && "pc-file-card--mobile",
                )}
              >
                <div className="pc-file-card__top">
                  <div className="pc-pdf-badge">PDF</div>

                  <div className="pc-file-card__info">
                    <div className="pc-file-card__name" title={msg.fileName}>
                      {msg.fileName}
                    </div>

                    <div className="pc-file-card__size">
                      {msg.fileSizeDisplay || formatFileSize(msg.fileSize)}
                    </div>
                  </div>
                </div>

                <div className="pc-file-card__actions">
                  <button
                    onClick={() => openPreview(msg.file)}
                    className="pc-link-btn"
                  >
                    Preview
                  </button>

                  <button
                    onClick={() => triggerDownload(msg.file, msg.fileName)}
                    className="pc-link-btn"
                  >
                    Download
                  </button>
                </div>

                <div className="pc-file-card__time">{msg.time}</div>
              </div>
            ) : (
              /* ================= OTHER FILES ================= */
              <div
                className={cx(
                  "pc-file-card",
                  isMe && "pc-file-card--me",
                  isMobile && "pc-file-card--mobile",
                )}
              >
                <div className="pc-file-card__name">{msg.fileName}</div>

                <div className="pc-file-card__size">
                  {msg.fileSizeDisplay || formatFileSize(msg.fileSize)}
                </div>

                <button
                  onClick={() => triggerDownload(msg.file, msg.fileName)}
                  className="pc-link-btn pc-link-btn--block"
                >
                  ⬇ Download
                </button>

                <div className="pc-file-card__time">{msg.time}</div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="pc-msg-sender">
              {String(msg.senderId || "") === String(authUserId || "")
                ? "You"
                : msg.sender}
              {isLockedDecision && (
                <span className="pc-badge pc-badge--locked" title="Locked">
                  🔒 Locked
                </span>
              )}
              {wasIMentioned && (
                <span
                  className="pc-badge pc-badge--mention"
                  title="You were mentioned"
                >
                  🔔 mentioned
                </span>
              )}
            </div>
            <div className="pc-msg-text">
              {renderWithMentions(msg.text || "")}
            </div>
          </>
        )}

        <div className="pc-msg-time">{msg.time}</div>
      </div>
    </div>
  );
});

/* ============================= Page ============================= */
const ProjectCommunication = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const userNavigatedBack = useRef(false);
  // projects state (keep high so it's available to effects)
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Compose: display & raw
  const [message, setMessage] = useState("");
  const [messageRaw, setMessageRaw] = useState("");

  const [postAsDiscussion, setPostAsDiscussion] = useState(false);
  const [discussionTopic, setDiscussionTopic] = useState("");

  // Decision compose state
  const [postAsDecision, setPostAsDecision] = useState(false);
  const [decisionTopic, setDecisionTopic] = useState("");
  const [decisionFinal, setDecisionFinal] = useState("");
  const [decisionDecideBy, setDecisionDecideBy] = useState(""); // yyyy-mm-dd
  const [decisionLock, setDecisionLock] = useState(false);
  const [decisionFile, setDecisionFile] = useState(null);

  const [editingMessageId, setEditingMessageId] = useState(null);
  const [chatMessages, setChatMessages] = useState({});
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [visibleProjects, setVisibleProjects] = useState([]);
  const [feedRefreshTick, setFeedRefreshTick] = useState(0);

  // chat filters: employee id, checklist, work type ("" = no filter)
  const [empFilter, setEmpFilter] = useState("");
  const [checklistFilter, setChecklistFilter] = useState("");
  const [workTypeFilter, setWorkTypeFilter] = useState("");

  const [isPending, startTransition] = useTransition();
  const { authUser } = useAuthStore();

  // refs
  const fileInputRef = useRef();
  const inputRef = useRef();
  const messagesEndRef = useRef();
  const rootRef = useRef(null);
  const emojiRef = useRef(null);

  // UI
  const [emojiOpen, setEmojiOpen] = useState(false);

  // Mentions UI
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);

  // Members for mentions
  const [members, setMembers] = useState([]);

  // dnd state
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  // responsive
  const [containerW, setContainerW] = useState(1200);
  useEffect(() => {
    if (!rootRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 1200;
      setContainerW(w);
    });
    ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, []);

  /* ---------- NEW: freeze page scroll + fit chat to the screen ----------
     Only the message list scrolls. The project header and the message
     box stay fixed. Works even when a parent layout wrapper scrolls. */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const touched = [];
    const lock = (node) => {
      if (!node) return;
      touched.push({ node, overflow: node.style.overflow });
      node.style.overflow = "hidden";
      node.scrollTop = 0;
    };

    lock(document.documentElement);
    lock(document.body);

    // lock every scrolling parent (layout wrapper / main container)
    let p = el.parentElement;
    while (p && p !== document.body) {
      const oy = window.getComputedStyle(p).overflowY;
      if (oy === "auto" || oy === "scroll") lock(p);
      p = p.parentElement;
    }
    window.scrollTo(0, 0);

    // height = visible screen height from where this page starts
    const fit = () => {
      const top = Math.max(el.getBoundingClientRect().top, 0);
      el.style.height = `${Math.max(window.innerHeight - top, 320)}px`;
    };
    fit();
    const raf = requestAnimationFrame(fit);
    window.addEventListener("resize", fit);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      touched.forEach(({ node, overflow }) => {
        node.style.overflow = overflow;
      });
      el.style.height = "";
    };
  }, []);

  const isMobile = containerW <= 768;
  const isDesktop = containerW >= 1024;

  const projectMembers = useMemo(() => members, [members]);

  // Router
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const incomingProjectId = useMemo(() => {
    return (
      location?.state?.openProjectId ||
      searchParams.get("projectId") ||
      searchParams.get("pid") ||
      null
    );
  }, [location, searchParams]);
  const incomingProjectName = useMemo(() => {
    const n =
      location?.state?.openProjectName ||
      searchParams.get("project") ||
      searchParams.get("name") ||
      null;
    return n ? String(n).trim() : null;
  }, [location, searchParams]);

  const normalizeName = (s) =>
    String(s || "")
      .trim()
      .toLowerCase();
  const normName = (p) => normalizeName(p?.projectName || p?.name);

  // ---------- CACHE + FETCH ----------
  const cacheRef = useRef(new Map());
  const fileSizeCache = useRef(new Map());
  const pendingFetchRef = useRef(new Map());

  const applyFileSizes = useCallback((grouped) => {
    const out = {};
    for (const dayKey of Object.keys(grouped)) {
      out[dayKey] = grouped[dayKey].map((m) => {
        const size = fileSizeCache.current.get(m._id);
        return size ? { ...m, fileSizeDisplay: size } : m;
      });
    }
    return out;
  }, []);

  // ---------- Assign Task: modal + immediate inject ----------
  const [assignTaskOpen, setAssignTaskOpen] = useState(false);

  const pushTaskToChat = useCallback(
    (projectId, taskMsg) => {
      if (!projectId || !taskMsg) return;

      const createdAtISO = taskMsg.createdAt || new Date().toISOString();
      const key = dayjs(createdAtISO).format("YYYY-MM-DD");

      const entry = {
        _id: taskMsg._id || `temp-task-${Date.now()}`,
        sender:
          taskMsg?.sender?.name ||
          taskMsg?.sender?.username ||
          taskMsg?.sender?.email ||
          "System",
        senderId: taskMsg?.sender?._id || null,
        text: taskMsg.content || "",
        type: "task",
        task: taskMsg.task || {
          assignedToId: taskMsg.assignedToId,
          assignedToName: taskMsg.assignedToName,
          typeofProject: taskMsg.typeofProject,
          workType: taskMsg.workType,
          drawingDocuments: taskMsg.drawingDocuments,
          estimatedHours: taskMsg.estimatedHours,
          dueDate: taskMsg.dueDate,
          remark: taskMsg.remark,
        },
        decision: null,
        replyTo: null,
        time: new Date(createdAtISO).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        createdAt: createdAtISO,
      };

      const grouped = cacheRef.current.get(projectId) || {};
      const arr = grouped[key] ? [...grouped[key], entry] : [entry];
      grouped[key] = arr.slice(-PAGE_SIZE);
      cacheRef.current.set(projectId, grouped);

      if (
        selectedProject &&
        String(selectedProject._id) === String(projectId)
      ) {
        setChatMessages(applyFileSizes(grouped));
        requestAnimationFrame(() =>
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }),
        );
      }
    },
    [selectedProject, applyFileSizes],
  );

  const handleTaskAssigned = useCallback(
    (createdTaskMsg) => {
      setAssignTaskOpen(false);

      if (createdTaskMsg && selectedProject?._id) {
        // inject immediately into chat
        pushTaskToChat(String(selectedProject._id), createdTaskMsg);
      }

      // still bump feed panel
      setFeedRefreshTick((n) => n + 1);
    },
    [selectedProject?._id, pushTaskToChat],
  );

  // clear selection when this page unmounts (prevents old project sticking)
  useEffect(() => {
    return () => {
      setSelectedProject(null);
    };
  }, []);

  // close emoji on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (!emojiRef.current) return;
      if (!emojiRef.current.contains(e.target)) setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // admin project connecter — re-select reliably when query/state changes
  useEffect(() => {
    if (!projects.length) return;

    const curId = selectedProject?._id ? String(selectedProject._id) : null;

    let next = null;

    // 1) by explicit id from state/query/localStorage
    if (incomingProjectId && String(incomingProjectId) !== curId) {
      next =
        projects.find((p) => String(p._id) === String(incomingProjectId)) ||
        null;
    }

    // 2) by explicit name from state/query (exact, then partial)
    if (!next && incomingProjectName) {
      const target = incomingProjectName.trim().toLowerCase();
      next =
        projects.find(
          (p) =>
            (p.projectName || p.name || "").trim().toLowerCase() === target,
        ) ||
        projects.find((p) =>
          (p.projectName || p.name || "").trim().toLowerCase().includes(target),
        ) ||
        null;

      // if it resolves to the same as current, don't flip-flop
      if (next && String(next._id) === curId) next = null;
    }

    if (next && !userNavigatedBack.current) {
      setSelectedProject(next);
    }
  }, [
    projects,
    incomingProjectId,
    incomingProjectName,
    selectedProject?._id, // 🔑 re-evaluate when current selection changes
  ]);

  const setFromCacheImmediately = useCallback(
    (projectId) => {
      const cached = cacheRef.current.get(projectId);
      if (cached) {
        setChatMessages(applyFileSizes(cached));
        setMessagesLoading(false);
      } else {
        setChatMessages({});
        setMessagesLoading(true);
      }
    },
    [applyFileSizes],
  );

  // Progressive fetch
  const fetchMessages = useCallback(
    async (projectId, { skipIfCached = false } = {}) => {
      if (!projectId) return;
      if (skipIfCached && cacheRef.current.has(projectId)) return;

      const prev = pendingFetchRef.current.get(projectId);
      prev?.abort();

      const controllerSmall = new AbortController();
      pendingFetchRef.current.set(projectId, controllerSmall);

      try {
        const small = await axiosInstance.get(
          `/project-messages/${projectId}/messages?limit=${FIRST_SNAPPY_BATCH}`,
          { signal: controllerSmall.signal },
        );
        const groupedSmall = fastNormalize(small.data || []);
        cacheRef.current.set(projectId, groupedSmall);

        setChatMessages((prev) => {
          if (
            !selectedProject ||
            String(selectedProject._id) !== String(projectId)
          )
            return prev;
          return applyFileSizes(groupedSmall);
        });
        if (
          selectedProject &&
          String(selectedProject._id) === String(projectId)
        ) {
          setMessagesLoading(false);
        }

        const controllerFull = new AbortController();
        pendingFetchRef.current.set(projectId, controllerFull);

        const full = await axiosInstance.get(
          `/project-messages/${projectId}/messages?limit=${PAGE_SIZE}`,
          { signal: controllerFull.signal },
        );
        const groupedFull = fastNormalize(full.data || []);
        cacheRef.current.set(projectId, groupedFull);

        setChatMessages((prev) => {
          if (
            !selectedProject ||
            String(selectedProject._id) !== String(projectId)
          )
            return prev;
          return applyFileSizes(groupedFull);
        });
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          console.error("fetchMessages error:", e);
        }
      } finally {
        pendingFetchRef.current.delete(projectId);
      }
    },
    [selectedProject, applyFileSizes],
  );

  const prefetchVisible = useCallback(() => {
    const ids = (visibleProjects || [])
      .slice(0, 4)
      .map((p) => p._id)
      .filter(Boolean);
    ids.forEach((id) => fetchMessages(id, { skipIfCached: true }));
  }, [visibleProjects, fetchMessages]);

  /* ---------- initial projects ---------- */
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/projects");
      const list = Array.isArray(res.data) ? res.data : [];
      setProjects(list);
      setVisibleProjects(list);
    } catch (e) {
      console.error("Error fetching projects:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // choose initial selected project (ID -> Name -> last)
  useEffect(() => {
    if (!projects.length || selectedProject || userNavigatedBack.current)
      return;

    // 1) Try by ID (state / query / localStorage)
    if (incomingProjectId) {
      const byId = projects.find(
        (proj) => String(proj._id) === String(incomingProjectId),
      );
      if (byId) {
        setSelectedProject(byId);
        return;
      }
    }

    // 2) Try by Name (state / query)
    if (incomingProjectName) {
      const target = normalizeName(incomingProjectName);
      const byName =
        projects.find((p) => normName(p) === target) ||
        projects.find((p) => normName(p).includes(target));
      if (byName) {
        setSelectedProject(byName);
        return;
      }
    }

    // 3) Fallback: last opened project
    // 3) Fallback only for desktop, not mobile
    if (!isMobile) {
      const lastId = localStorage.getItem("lastProjectId");
      if (lastId) {
        const last = projects.find(
          (proj) => String(proj._id) === String(lastId),
        );
        if (last) setSelectedProject(last);
      }
    }
  }, [
    projects,
    selectedProject,
    incomingProjectId,
    incomingProjectName,
    isMobile,
  ]);

  // persist last opened project
  useEffect(() => {
    if (selectedProject?._id) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    }
  }, [selectedProject?._id]);

  useEffect(() => {
    prefetchVisible();
  }, [prefetchVisible]);

  useEffect(() => {
    const onFocus = () => {
      fetchProjects();
      prefetchVisible();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchProjects, prefetchVisible]);

  /* ---------- selection / load ---------- */
  useEffect(() => {
    if (!selectedProject?._id) {
      setMembers([]);
      return;
    }
    setFromCacheImmediately(selectedProject._id);
    fetchMessages(selectedProject._id);
  }, [selectedProject?._id, setFromCacheImmediately, fetchMessages]);

  // filters belong to one project: reset them when another project opens
  useEffect(() => {
    setEmpFilter("");
    setChecklistFilter("");
    setWorkTypeFilter("");
  }, [selectedProject?._id]);

  /* ==========================================================
     PROJECT NOTIFICATION RECIPIENT IDS
     Send exact project users to backend with every message.
  ========================================================== */
  const notificationRecipientIds = useMemo(() => {
    const myId = String(authUser?._id || "");

    const ids = (members || [])
      .map((member) => String(member?._id || ""))
      .filter((id) => id && id !== myId);

    console.log("✅ FINAL RECIPIENT IDS:", ids);

    return ids;
  }, [members, authUser?._id]);

  // FETCH MEMBERS for mentions
  useEffect(() => {
    if (!selectedProject?._id) return setMembers([]);

    let cancelled = false;

    const normalize = (arr = []) =>
      (arr || [])
        .map((m) => ({
          _id:
            m?.user?._id ||
            m?.userId?._id ||
            m?.userId ||
            m?.employee?._id ||
            m?.employeeId?._id ||
            m?.employeeId ||
            m?._id ||
            m?.id ||
            "",
          name:
            m.name ||
            m.fullName ||
            m.displayName ||
            m.username ||
            m.email ||
            "Member",
        }))
        .filter((x) => x._id)
        .reduce((acc, cur) => {
          if (!acc.find((a) => a._id === cur._id)) acc.push(cur);
          return acc;
        }, []);

    // 🟢 STEP 1: Get members DIRECTLY from project
    const projectMembers =
      selectedProject.assignedUsers ||
      selectedProject.members ||
      selectedProject.participants ||
      selectedProject.users ||
      [];

    const normalizedMembers = normalize(projectMembers);

    if (normalizedMembers.length) {
      if (!cancelled) setMembers(normalizedMembers);
      return;
    }

    // 🟡 STEP 2: Fallback → Infer from messages in cache
    const inferred = [];
    const seen = new Set();

    const grouped = cacheRef.current.get(selectedProject._id) || {};

    Object.values(grouped).forEach((day) =>
      day.forEach((m) => {
        const id = m.senderId || "";
        const name = m.sender || "";
        if (id && !seen.has(id)) {
          seen.add(id);
          inferred.push({ _id: id, name });
        }

        const t = m.task;
        if (t?.assignedToId && t?.assignedToName && !seen.has(t.assignedToId)) {
          seen.add(t.assignedToId);
          inferred.push({ _id: t.assignedToId, name: t.assignedToName });
        }
      }),
    );

    if (!cancelled) setMembers(inferred);

    return () => {
      cancelled = true;
    };
  }, [selectedProject]);

  // auto-scroll on updates
  const firstLoadRef = useRef(true);

  useEffect(() => {
    if (!messagesEndRef.current) return;

    if (firstLoadRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" }); // ⛔ no animation
      firstLoadRef.current = false;
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" }); // ✅ new msg only
    }
  }, [chatMessages]);

  useEffect(() => {
    if (selectedProject?._id) {
      firstLoadRef.current = true; // 🔑 VERY IMPORTANT
    }
  }, [selectedProject?._id]);

  /* ---------- realtime: project assign/remove ---------- */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onAdded = (p) => {
      setProjects((prev) =>
        prev.some((x) => String(x._id) === String(p._id)) ? prev : [p, ...prev],
      );
      setVisibleProjects((prev) =>
        prev.some((x) => String(x._id) === String(p._id)) ? prev : [p, ...prev],
      );
    };

    const onRemoved = ({ _id }) => {
      setProjects((prev) => prev.filter((x) => String(x._id) !== String(_id)));
      setVisibleProjects((prev) =>
        prev.filter((x) => String(x._id) !== String(_id)),
      );
      setSelectedProject((sp) =>
        sp && String(sp._id) === String(_id) ? null : sp,
      );
    };

    socket.on("project:added", onAdded);
    socket.on("project:removed", onRemoved);
    return () => {
      socket.off("project:added", onAdded);
      socket.off("project:removed", onRemoved);
    };
  }, []);

  /* ---------- realtime: join chat room + incoming ---------- */
  useEffect(() => {
    if (!selectedProject?._id) return;
    const socket = getSocket();
    if (!socket) return;

    socket.emit("project:join", { projectId: selectedProject._id });

    const appendToCache = (projId, entry, createdAtISO) => {
      const grouped = cacheRef.current.get(projId) || {};
      const key = dayjs(createdAtISO).format("YYYY-MM-DD");
      const arr = grouped[key] ? [...grouped[key], entry] : [entry];
      grouped[key] = arr.slice(-Math.max(PAGE_SIZE, 1));
      const keys = Object.keys(grouped).sort();
      if (keys.length > 10) delete grouped[keys[0]];
      cacheRef.current.set(projId, grouped);
      if (selectedProject && String(selectedProject._id) === projId) {
        setChatMessages(applyFileSizes(grouped));
      }
    };

    const onIncoming = (msg) => {
      const projId = String(msg.project || "");
      const createdAtISO = msg.createdAt || new Date().toISOString();

      const entry = {
        _id: msg._id,
        sender:
          msg?.sender?.name ||
          msg?.sender?.username ||
          msg?.sender?.email ||
          msg?.senderLabel ||
          "System",
        senderId:
          msg?.sender?._id || msg?.senderId?._id || msg?.senderId || null,
        text: msg.content,
        file: msg.file,
        fileName: msg.fileName,
        fileSize: msg.fileSize,
        // UPDATED: robust inference
        type: inferType(msg),
        task:
          msg.type === "task"
            ? {
                assignedToId: msg.assignedToId || msg.assignedTo,
                assignedToName: msg.assignedToName,
                typeofProject: msg.typeofProject,
                workType: msg.workType,
                drawingDocuments: msg.drawingDocuments,
                estimatedHours: msg.estimatedHours,
                dueDate: msg.dueDate,
                remark: msg.remark,
              }
            : null,
        decision: msg.decision || null,
        replyTo: null,
        time: new Date(createdAtISO).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        createdAt: createdAtISO,
      };

      appendToCache(projId, entry, createdAtISO);

      // Desktop notification for incoming project-wise communication.
      showProjectDesktopNotification({
        message: msg,
        project: selectedProject,
        myUserId: authUser?._id,
      });

      if (entry.type === "task") setFeedRefreshTick((n) => n + 1);
    };

    socket.on("project:message", onIncoming);

    return () => {
      socket.off("project:message", onIncoming);
      socket.emit("project:leave", { projectId: selectedProject._id });
    };
  }, [selectedProject?._id, applyFileSizes, selectedProject, authUser?._id]);

  /* ---------- optimistic UI for text/file ---------- */
  const addOptimistic = useCallback(
    (payload = {}) => {
      const createdAtISO = new Date().toISOString();
      const key = dayjs(createdAtISO).format("YYYY-MM-DD");

      const optimistic = {
        _id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        sender: "You",
        senderId: authUser?._id || null,
        text: payload.text || "",
        file: payload.file || null,
        fileName: payload.fileName || null,
        fileSize: payload.fileSize || null,
        type: payload.type || null,
        decision: payload.decision || null,
        replyTo: payload.replyTo
          ? {
              sender: payload.replyTo.sender,
              text: payload.replyTo.text,
              fileName: payload.replyTo.fileName,
              _id: payload.replyTo._id,
            }
          : null,
        time: new Date(createdAtISO).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        createdAt: createdAtISO,
        _optimistic: true,
      };

      if (selectedProject?._id) {
        const grouped = cacheRef.current.get(selectedProject._id) || {};
        const arr = grouped[key] ? [...grouped[key], optimistic] : [optimistic];
        grouped[key] = arr.slice(-PAGE_SIZE);
        cacheRef.current.set(selectedProject._id, grouped);
        setChatMessages(applyFileSizes(grouped));
      }

      requestAnimationFrame(() =>
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }),
      );
    },
    [authUser?._id, selectedProject?._id, applyFileSizes],
  );

  /* ---------- actions (post / upload / download) ---------- */
  const [replyingTo, setReplyingTo] = useState(null);

  const insertMention = useCallback(
    (member) => {
      if (!inputRef.current) return;
      const el = inputRef.current;
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;

      const probe = matchMentionAtCursor(message, start);

      const displayToken = `@${member.name}`;
      const rawToken = makeMentionToken(member.name, member._id);

      let before = message.slice(0, start);
      let after = message.slice(end);

      let beforeRaw = messageRaw.slice(0, start);
      let afterRaw = messageRaw.slice(end);

      if (probe) {
        before = message.slice(0, probe.start);
        after = message.slice(start);
        beforeRaw = messageRaw.slice(0, probe.start);
        afterRaw = messageRaw.slice(start);
      }

      const nextDisplay = `${before}${displayToken} ${after}`;
      const nextRaw = `${beforeRaw}${rawToken} ${afterRaw}`;

      setMessage(nextDisplay);
      setMessageRaw(nextRaw);
      setMentionOpen(false);
      setMentionQuery("");
      setMentionIndex(0);

      const caret = (before + displayToken + " ").length;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    },
    [message, messageRaw],
  );

  const handlePost = async () => {
    if (!selectedProject) return;

    /* ----- DECISION MODE ----- */
    if (postAsDecision && !replyingTo) {
      if (!decisionTopic.trim() && !decisionFinal.trim()) return;

      // optimistic show
      addOptimistic({
        text: `Topic: ${decisionTopic}\n\nFinal Decision:\n${decisionFinal}`,
        type: "decision",
        decision: {
          topic: decisionTopic,
          finalDecision: decisionFinal,
          decideBy: decisionDecideBy || null,
          lock: !!decisionLock,
          byName: authUser?.name || "",
          lockedBy: decisionLock ? authUser?.name || "" : "",
        },
        file: decisionFile ? URL.createObjectURL(decisionFile) : null,
        fileName: decisionFile?.name || null,
        fileSize: decisionFile?.size || null,
      });

      // upload file (optional) + post
      let uploaded = null;
      try {
        if (decisionFile) {
          const fd = new FormData();
          fd.append("file", decisionFile);
          uploaded = await axiosInstance.post(
            `/project-messages/${selectedProject._id}/upload`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
        }

        const rawBody = displayToRaw(decisionFinal, projectMembers);
        const mentionIds = extractMentionIds(rawBody);

        const payload = {
          type: "decision",
          content:
            `Topic: ${decisionTopic}\n\n` +
            `Final Decision:\n${rawBody}\n` +
            (decisionDecideBy ? `\nDecideBy: ${decisionDecideBy}` : "") +
            `\nLock: ${decisionLock ? "true" : "false"}`,
          decision: {
            topic: decisionTopic || "",
            finalDecision: rawBody || "",
            decideBy: decisionDecideBy || null,
            lock: !!decisionLock,
            byName: authUser?.name || "",
            lockedBy: decisionLock ? authUser?.name || "" : "",
          },
          file: uploaded?.data?.fileKey || null,
          fileName: uploaded?.data?.fileName || null,
          fileSize: uploaded?.data?.fileSize || null,
          replyTo: null,
          mentions: mentionIds,
          notificationRecipientIds,
        };

        await axiosInstance.post(
          `/project-messages/${selectedProject._id}/messages`,
          payload,
        );
      } catch (e) {
        console.error("Error posting decision:", e);
      } finally {
        // clear decision compose
        setDecisionTopic("");
        setDecisionFinal("");
        setDecisionDecideBy("");
        setDecisionLock(false);
        setDecisionFile(null);
      }
      return;
    }

    /* ----- NORMAL/DISCUSSION MODE ----- */
    if (!message.trim()) return;
    const type = postAsDiscussion ? "discussion" : undefined;

    const contentRaw =
      postAsDiscussion && discussionTopic && !replyingTo
        ? `Topic: ${discussionTopic}\n\n${
            messageRaw || displayToRaw(message, projectMembers)
          }`
        : messageRaw || displayToRaw(message, projectMembers);

    const mentionIds = extractMentionIds(contentRaw);

    if (!editingMessageId) {
      addOptimistic({ text: contentRaw, type, replyTo: replyingTo });
    }

    try {
      if (editingMessageId) {
        await axiosInstance.put(
          `/project-messages/${selectedProject._id}/messages/${editingMessageId}`,
          {
            content: contentRaw,
            type,
            replyTo: replyingTo?._id || null,
            mentions: mentionIds,
            notificationRecipientIds,
          },
        );
        setEditingMessageId(null);
      } else {
        console.log("📁 SELECTED PROJECT RAW:", selectedProject);
        console.log("👥 MEMBERS RAW:", members);
        console.log(
          "🔔 FRONTEND PROJECT RECIPIENT IDS:",
          notificationRecipientIds,
        );

        await axiosInstance.post(
          `/project-messages/${selectedProject._id}/messages`,
          {
            content: contentRaw,
            type,
            replyTo: replyingTo?._id || null,
            mentions: mentionIds,
            notificationRecipientIds,
          },
        );
      }

      // Clear UI state
      setMessage("");
      setMessageRaw("");
      setDiscussionTopic("");
      setEmojiOpen(false);
      setReplyingTo(null);
      setMentionOpen(false);
      setMentionQuery("");
      setMentionIndex(0);
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.blur();
        requestAnimationFrame(() => {
          inputRef.current.focus();
          inputRef.current.setSelectionRange(0, 0);
        });
      }
    } catch (e) {
      console.error(e);
      setTimeout(
        () => fetchMessages(selectedProject._id, { skipIfCached: false }),
        400,
      );
    }
  };

  const uploadAndPostFile = async (file) => {
    if (!file || !selectedProject) return;

    // In Decision mode we only stage the file into the decision composer
    if (postAsDecision && !replyingTo) {
      setDecisionFile(file);
      return;
    }

    const baseCaption = `📎 ${file.name}`;
    const caption =
      postAsDiscussion && discussionTopic && !replyingTo
        ? `Topic: ${discussionTopic}\n\n${baseCaption}`
        : baseCaption;

    addOptimistic({
      text: caption,
      file: "__uploading__", // temporary placeholder
      fileName: file.name,
      fileSize: file.size,
      replyTo: replyingTo,
      type: postAsDiscussion && !replyingTo ? "discussion" : undefined,
    });

    const fd = new FormData();
    fd.append("file", file);

    try {
      const up = await axiosInstance.post(
        `/project-messages/${selectedProject._id}/upload`,
        fd,
      );

      const serverCaption =
        postAsDiscussion && discussionTopic && !replyingTo
          ? `Topic: ${discussionTopic}\n\n📎 ${up.data.fileName}`
          : `📎 ${up.data.fileName}`;

      await axiosInstance.post(
        `/project-messages/${selectedProject._id}/messages`,
        {
          content: serverCaption,
          file: up.data.fileKey, // ✅ ONLY KEY SAVE KARO
          fileName: up.data.fileName,
          fileSize: up.data.fileSize,
          replyTo: replyingTo?._id || null,
          type: postAsDiscussion && !replyingTo ? "discussion" : undefined,
          mentions: [],
          notificationRecipientIds,
        },
      );
    } catch (e) {
      console.error(e);
      alert("❌ File upload failed.");
    } finally {
      setReplyingTo(null);
      setDiscussionTopic("");
      setTimeout(
        () => fetchMessages(selectedProject._id, { skipIfCached: false }),
        200,
      );
    }
  };

  const uploadMany = async (files) => {
    for (const f of files) {
      await uploadAndPostFile(f);
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (postAsDecision && !replyingTo) {
      setDecisionFile(files[0]);
    } else {
      await uploadMany(files);
    }
    e.target.value = "";
  };
  const triggerDownload = async (fileKey, fileName) => {
    try {
      const token = localStorage.getItem("token"); // 🔑 JWT

      const downloadUrl = `${SERVER_ORIGIN}/api/download?key=${encodeURIComponent(
        fileKey,
      )}&name=${encodeURIComponent(fileName || "")}`;

      const res = await fetch(downloadUrl, {
        method: "GET",
        credentials: "include",
        headers: token
          ? {
              Authorization: `Bearer ${token}`, // ⭐ MOST IMPORTANT
            }
          : {},
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Download failed:", res.status, text);
        alert("❌ Download failed");
        return;
      }

      const blob = await res.blob();

      // 🚨 HTML / JSON safety
      if (
        blob.type.includes("text/html") ||
        blob.type.includes("application/json")
      ) {
        alert("❌ Download failed (auth or route issue)");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      alert("❌ Download failed");
    }
  };
  /* ---------- dnd handlers ---------- */
  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedProject) return;
    dragCounter.current += 1;
    setDragActive(true);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedProject) return;
    if (!dragActive) setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedProject) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      setDragActive(false);
      dragCounter.current = 0;
    }
  };
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setDragActive(false);
    if (!selectedProject) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;
    await uploadMany(files);
  };

  // Paste-to-upload
  const onPaste = async (e) => {
    if (!selectedProject) return;
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (files.length) {
      e.preventDefault();
      await uploadMany(files);
    }
  };

  /* ---------- file size lazy fetch ---------- */
  const ensureSize = async (msg) => {
    if (msg.fileSize || !msg.file || fileSizeCache.current.has(msg._id)) return;
    const size = await getFileSizeFromURL(msg.file);
    if (size) {
      fileSizeCache.current.set(msg._id, size);
      setChatMessages((prev) => {
        const out = {};
        for (const k of Object.keys(prev)) {
          out[k] = prev[k].map((m) =>
            m._id === msg._id ? { ...m, fileSizeDisplay: size } : m,
          );
        }
        return out;
      });
    }
  };

  /* ---------- message context menu ---------- */
  const openMsgMenu = (e, msg) => {
    e.preventDefault();
    const opts = [
      { label: "Reply", action: () => setReplyingTo(msg) },
      {
        label: "Copy",
        action: async () => {
          const text = msg.text || msg.fileName || "";
          try {
            await navigator.clipboard.writeText(text);
          } catch {}
        },
      },
    ];

    if (msg.file) {
      opts.push({
        label: "Download",
        action: () => triggerDownload(msg.file, msg.fileName),
      });
    }
    createContextMenu(e.clientX, e.clientY, opts);
  };

  /* ---------- build thread index ---------- */
  const { repliesIndex, hiddenIds } = useMemo(() => {
    const index = new Map();
    const hide = new Set();
    const all = [];
    Object.values(chatMessages).forEach((arr) => all.push(...arr));

    const isRoot = (m) =>
      (m.type === "discussion" ||
        (looksLikeDiscussionText(m.text) && !looksLikeDecisionText(m.text))) &&
      !m.replyTo;

    const byId = new Map(all.map((m) => [m._id, m]));

    all.forEach((m) => {
      if (m.replyTo && m.replyTo._id && byId.has(m.replyTo._id)) {
        const parent = byId.get(m.replyTo._id);
        if (isRoot(parent)) {
          if (!index.has(parent._id)) index.set(parent._id, []);
          index.get(parent._id).push(m);
          hide.add(m._id);
        }
      }
    });

    for (const [, list] of index) {
      list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }
    return { repliesIndex: index, hiddenIds: hide };
  }, [chatMessages]);

  /* ---------- filters: options, matching and counts ---------- */
  const filtersActive = Boolean(empFilter || checklistFilter || workTypeFilter);

  // dropdown options built from the messages loaded for this project
  const filterOptions = useMemo(() => {
    const employees = new Map();
    const workTypes = new Set();
    const checklists = new Set();
    const myId = String(authUser?._id || "");

    Object.values(chatMessages).forEach((list) => {
      list.forEach((m) => {
        const id = String(m.senderId || "");
        if (id) {
          const name = id === myId ? authUser?.name || m.sender : m.sender;
          if (name && name !== "You") employees.set(id, name);
          else if (!employees.has(id))
            employees.set(id, authUser?.name || "You");
        }

        if (m.type === "employee-log") {
          const meta = getEntryMeta(m.text);
          if (meta.work) workTypes.add(meta.work);
          if (meta.filledChecklist) checklists.add(meta.filledChecklist);
        }
      });
    });

    const byText = (a, b) => a.localeCompare(b);

    return {
      employees: [...employees.entries()]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => byText(a.name, b.name)),
      workTypes: [...workTypes].sort(byText),
      checklists: [...checklists].sort(byText),
    };
  }, [chatMessages, authUser?._id, authUser?.name]);

  const matchesFilters = useCallback(
    (msg) => {
      if (empFilter && String(msg.senderId || "") !== empFilter) return false;

      // checklist and work type only exist on Employee Work Entry cards
      if (checklistFilter || workTypeFilter) {
        if (msg.type !== "employee-log") return false;

        const meta = getEntryMeta(msg.text);
        const hasAnyChecklist = Boolean(
          meta.filledChecklist || meta.hasUploadedChecklist,
        );

        if (workTypeFilter && meta.work !== workTypeFilter) return false;

        if (checklistFilter === FILTER_ANY_CHECKLIST && !hasAnyChecklist)
          return false;
        if (checklistFilter === FILTER_NO_CHECKLIST && hasAnyChecklist)
          return false;
        if (
          checklistFilter &&
          checklistFilter !== FILTER_ANY_CHECKLIST &&
          checklistFilter !== FILTER_NO_CHECKLIST &&
          meta.filledChecklist !== checklistFilter
        )
          return false;
      }

      return true;
    },
    [empFilter, checklistFilter, workTypeFilter],
  );

  // messages shown after filters (thread replies are shown inside their discussion)
  const { shownCount, totalCount } = useMemo(() => {
    let shown = 0;
    let total = 0;
    Object.values(chatMessages).forEach((list) => {
      list.forEach((m) => {
        if (hiddenIds.has(m._id)) return;
        total += 1;
        if (matchesFilters(m)) shown += 1;
      });
    });
    return { shownCount: shown, totalCount: total };
  }, [chatMessages, hiddenIds, matchesFilters]);

  const clearFilters = () => {
    setEmpFilter("");
    setChecklistFilter("");
    setWorkTypeFilter("");
  };

  /* ---------- render ---------- */
  const mentionChoices = useMemo(() => {
    const q = (mentionQuery || "").toLowerCase();
    const list = projectMembers || [];
    if (!q) return list.slice(0, 8);
    return list.filter((m) => m.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionQuery, projectMembers]);

  const projectTitle =
    selectedProject?.projectName || selectedProject?.name || "";

  return (
    <div
      ref={rootRef}
      className={cx(
        "pc-root",
        isDesktop && "pc-root--desktop",
        isMobile && "pc-root--mobile",
      )}
    >
      {/* Left: Projects */}
      <div
        className={cx(
          "pc-left",
          !isDesktop && selectedProject && "pc-left--noborder",
          isMobile && selectedProject && "pc-left--hidden",
        )}
      >
        <ProjectsSidebar
          projects={projects}
          loading={loading}
          selectedProject={selectedProject}
          setSelectedProject={(p) =>
            startTransition(() => setSelectedProject(p))
          }
          refreshProjects={fetchProjects}
          isMobile={isMobile}
          onVisibleProjectsChange={(list) => {
            setVisibleProjects(list || []);
            setTimeout(prefetchVisible, 0);
          }}
          onAfterAssign={() => setFeedRefreshTick((n) => n + 1)}
          initialOpenProjectId={incomingProjectId}
          initialOpenProjectName={incomingProjectName}
        />
      </div>

      {/* Center: Chat */}
      <div
        className="pc-center"
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* NEW: frozen project header */}
        <div className="pc-header">
          {isMobile && selectedProject && (
            <button
              type="button"
              className="pc-back-btn"
              onClick={() => {
                userNavigatedBack.current = true;
                startTransition(() => setSelectedProject(null));

                setTimeout(() => {
                  userNavigatedBack.current = false; // allow future auto open
                }, 600);
              }}
              aria-label="Back to projects"
            >
              ←
            </button>
          )}

          {selectedProject ? (
            <div className="pc-header__inner">
              <div className="pc-header__avatar">
                {projectTitle.charAt(0).toUpperCase() || "P"}
              </div>
              <div className="pc-header__text">
                <div className="pc-header__title" title={projectTitle}>
                  {projectTitle}
                </div>
                <div className="pc-header__sub">
                  {members.length > 0
                    ? `${members.length} member${members.length > 1 ? "s" : ""}`
                    : "Project communication"}
                </div>
              </div>
            </div>
          ) : (
            <div className="pc-header__title">
              Select a project to start chatting
            </div>
          )}
        </div>

        {!selectedProject ? (
          <div className="pc-empty">No project selected</div>
        ) : (
          <>
            {/* Filters: employee, checklist, work type */}
            <div className="pc-filters">
              <div className="pc-filters__field">
                <label className="pc-filters__label" htmlFor="pc-filter-emp">
                  Employee
                </label>
                <select
                  id="pc-filter-emp"
                  className={cx(
                    "pc-filters__select",
                    empFilter && "pc-filters__select--active",
                  )}
                  value={empFilter}
                  onChange={(e) => setEmpFilter(e.target.value)}
                >
                  <option value="">All employees</option>
                  {filterOptions.employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pc-filters__field">
                <label className="pc-filters__label" htmlFor="pc-filter-work">
                  Work Type
                </label>
                <select
                  id="pc-filter-work"
                  className={cx(
                    "pc-filters__select",
                    workTypeFilter && "pc-filters__select--active",
                  )}
                  value={workTypeFilter}
                  onChange={(e) => setWorkTypeFilter(e.target.value)}
                >
                  <option value="">All work types</option>
                  {filterOptions.workTypes.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="pc-filters__clear"
                onClick={clearFilters}
                disabled={!filtersActive}
              >
                Clear
              </button>

              {filtersActive && (
                <span className="pc-filters__count">
                  Showing {shownCount} of {totalCount}
                </span>
              )}
            </div>

            <div
              className="pc-messages"
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              {messagesLoading && Object.keys(chatMessages).length === 0 && (
                <>
                  <div className="pc-skeleton" />
                  <div className="pc-skeleton" />
                  <div className="pc-skeleton" />
                </>
              )}

              {filtersActive && !messagesLoading && shownCount === 0 && (
                <div className="pc-filters__empty">
                  No messages match the selected filters.
                </div>
              )}

              {Object.keys(chatMessages).map((dateKey) => {
                const dayMessages = chatMessages[dateKey].filter(
                  (msg) => !hiddenIds.has(msg._id) && matchesFilters(msg),
                );

                // hide the date heading when nothing is left for that day
                if (!dayMessages.length) return null;

                return (
                  <div key={dateKey}>
                    <div className="pc-date">{formatDateGroup(dateKey)}</div>

                    {dayMessages.map((msg) => {
                      const isMe =
                        String(msg.senderId || "") ===
                        String(authUser?._id || "");
                      const thread =
                        (msg.type === "discussion" ||
                          looksLikeDiscussionText(msg.text)) &&
                        !msg.replyTo
                          ? repliesIndex.get(msg._id) || []
                          : [];
                      return (
                        <MessageRow
                          key={msg._id}
                          msg={msg}
                          isMe={isMe}
                          isMobile={isMobile}
                          onContext={openMsgMenu}
                          ensureSize={ensureSize}
                          triggerDownload={triggerDownload}
                          authUserId={authUser?._id}
                          onReply={(m) => setReplyingTo(m)}
                          threadReplies={thread}
                        />
                      );
                    })}
                  </div>
                );
              })}

              <div ref={messagesEndRef} />
            </div>

            {selectedProject && (
              <div className="pc-footer">
                {replyingTo && (
                  <div className="pc-replying">
                    Replying to <strong>{replyingTo.sender}</strong>:{" "}
                    {replyingTo.text || replyingTo.fileName || "Discussion"}
                    <span
                      className="pc-replying__close"
                      onClick={() => setReplyingTo(null)}
                    >
                      ❌
                    </span>
                  </div>
                )}

                {/* Decision composer */}
                {postAsDecision && !replyingTo && (
                  <div className="pc-composer-decision">
                    <div className="pc-composer-decision__title">
                      🧭 Decision
                    </div>

                    {/* Topic */}
                    <label className="pc-field-label">Topic</label>
                    <input
                      value={decisionTopic}
                      onChange={(e) => setDecisionTopic(e.target.value)}
                      placeholder="Write topic..."
                      className="pc-input"
                    />

                    {/* By (current user) */}
                    <div className="pc-composer-by">
                      By: <span>{authUser?.name || "—"}</span>
                    </div>

                    {/* Final Decision */}
                    <label className="pc-field-label">Final Decision</label>
                    <textarea
                      value={decisionFinal}
                      onChange={(e) => setDecisionFinal(e.target.value)}
                      placeholder="Type the final decision..."
                      rows={3}
                      className="pc-textarea"
                    />

                    {/* Decide by + Lock + Locked by */}
                    <div className="pc-composer-row">
                      <div>
                        <div className="pc-field-label">Decide by</div>
                        <input
                          type="date"
                          value={decisionDecideBy}
                          onChange={(e) => setDecisionDecideBy(e.target.value)}
                          className="pc-date-input"
                        />
                      </div>

                      <label
                        className={cx(
                          "pc-lock-toggle",
                          decisionLock && "pc-lock-toggle--on",
                        )}
                        title="Lock this decision"
                      >
                        <input
                          type="checkbox"
                          checked={decisionLock}
                          onChange={(e) => setDecisionLock(e.target.checked)}
                        />
                        🔒 Lock
                      </label>

                      {decisionLock && (
                        <span className="pc-locked-by">
                          Locked by: {authUser?.name || "—"}
                        </span>
                      )}
                    </div>

                    {/* File Attach */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      className="pc-attach-btn"
                      title="Attach file"
                    >
                      📎 File
                    </button>
                    {decisionFile && (
                      <span className="pc-attach-name">
                        {decisionFile.name}
                      </span>
                    )}
                  </div>
                )}

                {/* Discussion topic input when starting new discussion */}
                {postAsDiscussion && !replyingTo && (
                  <div className="pc-topic-row">
                    <input
                      value={discussionTopic}
                      onChange={(e) => setDiscussionTopic(e.target.value)}
                      placeholder="Discussion topic (optional)"
                      className="pc-topic-input"
                    />
                  </div>
                )}

                <div className="pc-input-row">
                  {/* Emoji panel */}
                  <div
                    ref={emojiRef}
                    className={cx(
                      "pc-emoji-panel",
                      emojiOpen && "pc-emoji-panel--open",
                    )}
                  >
                    <div className="pc-emoji-grid">
                      {EMOJIS.map((e) => (
                        <button
                          key={e}
                          type="button"
                          onClick={() => {
                            setMessage((m) => m + e);
                            setMessageRaw((r) => r + e);
                            inputRef.current?.focus();
                          }}
                          className="pc-emoji-btn"
                          title={e}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Mentions dropdown */}
                  <MentionList
                    open={mentionOpen}
                    items={mentionChoices}
                    activeIndex={mentionIndex}
                    onPick={insertMention}
                  />

                  {/* Main text input */}
                  <input
                    ref={inputRef}
                    name="project-chat-message"
                    autoComplete="off"
                    value={message}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMessage(val);
                      setMessageRaw(displayToRaw(val, projectMembers));

                      const caret = e.target.selectionStart || 0;
                      const probe = matchMentionAtCursor(val, caret);
                      if (probe) {
                        setMentionOpen(true);
                        setMentionQuery(probe.query || "");
                        setMentionIndex(0);
                      } else {
                        setMentionOpen(false);
                        setMentionQuery("");
                        setMentionIndex(0);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (mentionOpen) {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setMentionIndex((i) =>
                            Math.min(
                              i + 1,
                              Math.max(mentionChoices.length - 1, 0),
                            ),
                          );
                          return;
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setMentionIndex((i) => Math.max(i - 1, 0));
                          return;
                        }
                        if (e.key === "Enter") {
                          const item = mentionChoices[mentionIndex];
                          if (item) {
                            e.preventDefault();
                            insertMention(item);
                            return;
                          }
                        }
                        if (e.key === "Escape") {
                          setMentionOpen(false);
                          setMentionQuery("");
                          setMentionIndex(0);
                          return;
                        }
                      }

                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handlePost();
                      }
                    }}
                    onPaste={onPaste}
                    placeholder={
                      postAsDecision
                        ? "You’re composing a Decision above…"
                        : replyingTo
                          ? "Reply to discussion..."
                          : "Type a message..."
                    }
                    className="pc-main-input"
                    disabled={postAsDecision && !replyingTo}
                  />

                  {/* Group: Discussion + Decision + Assign Task (in order) */}
                  <div className="pc-toggle-group">
                    <button
                      type="button"
                      className={cx(
                        "pc-toggle-btn",
                        postAsDiscussion && "pc-toggle-btn--active",
                      )}
                      onClick={() => {
                        setPostAsDiscussion((s) => !s);
                        if (!postAsDiscussion) setPostAsDecision(false);
                      }}
                      title="Post as Discussion"
                    >
                      🗣️ Discussion
                    </button>

                    <button
                      type="button"
                      className={cx(
                        "pc-toggle-btn",
                        postAsDecision && "pc-toggle-btn--active",
                      )}
                      onClick={() => {
                        setPostAsDecision((s) => !s);
                        if (!postAsDecision) setPostAsDiscussion(false);
                      }}
                      title="Post as Decision"
                    >
                      🧭 Decision
                    </button>

                    <button
                      type="button"
                      className="pc-action-btn"
                      onClick={() => setAssignTaskOpen(true)}
                      title="Assign Task"
                    >
                      ✅ Assign Task
                    </button>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="pc-hidden"
                    multiple={!postAsDecision || !!replyingTo}
                  />

                  {/* Emoji */}
                  <button
                    type="button"
                    onClick={() => setEmojiOpen((s) => !s)}
                    className="pc-icon-btn"
                    title="Emoji"
                  >
                    <Smile size={18} />
                  </button>

                  {/* Attach file */}
                  <button
                    onClick={() => fileInputRef.current.click()}
                    className="pc-icon-btn"
                    title="Attach file"
                  >
                    <FileText size={18} />
                  </button>

                  <button onClick={handlePost} className="pc-send-btn">
                    {editingMessageId ? "Update" : "Send"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Right: feed */}
      <div className="pc-right">
        <TasksFeedPanel
          visibleProjects={visibleProjects}
          refreshSignal={feedRefreshTick}
          // filters
          projectIdFilter={selectedProject?._id}
          assigneeFilter={(selectedProject?.members || []).map((m) => m._id)}
        />
      </div>

      {/* Assign Task Modal */}
      {assignTaskOpen && selectedProject && (
        <AssignTaskModal
          project={selectedProject}
          onClose={() => setAssignTaskOpen(false)}
          onAssigned={handleTaskAssigned}
          users={[]} // modal can fetch internally
          drawingMaster={[]} // keep same shape
        />
      )}
    </div>
  );
};

export default ProjectCommunication;
